import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import SlateEditor from '../../../TextEditor/TextEditor';
import { Button, Modal } from '../../../_custom';
import LoadingSpinner from '../../../_custom/LoadingSpinner/LoadingSpinner';
import styles from './DealSummaryEditor.module.scss';
import { useReactToPrint } from 'react-to-print';
import html3pdf from 'html3pdf';
import {
    getSignedUrl,
    saveDocMetaDataInDocService,
    uploadFile,
} from '../../../../modules/Company/UploadJourney/components/UploadCard/utils/api/helpers';
import { toast } from 'react-toastify';
import { getTimeStampForFileName } from '../../../../utils/dateUtils';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { fetchImageFromS3AndConvertToBase64 } from '../../../../utils/s3';
import { aicaGreyBase64_2, regenerateIcon } from '../../../../assets/hostedassets';
import { Descendant } from 'slate';
import dayjs from 'dayjs';
import { TIME_DATE_FORMAT } from '../../../../utils/constants/commonConstants';
import { closeCommentsThread, resetAndHideCommentsThread } from '../../../../store/commentsThread/commentsThread_actions';
import { Mixpanel } from '../../../../utils/mixpanel';
import { getSubdomain } from '../../../../utils/utils';
import {
    AICA_ONEPAGER_CLOSED,
    AICA_ONEPAGER_PREVIEW,
    AICA_ONEPAGER_REGENERATE,
} from '../../../../utils/constants/mixpanelEvents/investorEvents';
import { TEXT_EDITOR_TYPES } from '../../../../store/investor/constants';
import { saveOnePagerTemplateData } from '../../../../store/investor/investorApiHelper';
import { slateToHtml } from '../../../TextEditor/utils/SlateSerialisationUtil';
import { getPdfExportStyles, getDefaultPdfOptions } from '../../../TextEditor/utils/pdfExportStyles';

const CREATE_DEAL = 'CREATE_DEAL';

function DealSummaryEditor({
    source,
    openModal,
    closeModal,
    categoryId,
    flowId,
    assesseeOrgId,
    metadataUpdate,
    generating,
    editorInitialData,
    editorReady,
    savingData,
    saveOnePagerTemplateDataForOrg,
    setShowRegenConfirmModal,
    userInfo,
    companyNameProp,
}) {
    const dispatch = useAppDispatch();
    const [saving, setSaving] = useState(false);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const contentToPrint = useRef(null);
    const editorContent = useRef<Descendant[]>([]);
    const companyName = companyNameProp || useAppSelector((state) => state.investor.dealApplicationDrawer.subHeading);
    const domainMetadata = useAppSelector((state) => state.user.domainMetadata);
    const user = useAppSelector((state) => state.user.userData);
    const mainImageUrl = domainMetadata?.mainImageUrl;
    const orgName = domainMetadata?.orgName;
    const [triggerSave, setTriggerSave] = useState(false);

    useEffect(() => {
        dispatch(resetAndHideCommentsThread());
        const events = { 'onepager-autosave': () => save() };
        Object.entries(events).forEach(([event, handler]) => addEventListener(event, handler));
        return () => Object.entries(events).forEach(([event, handler]) => removeEventListener(event, handler));
    }, []);

    useEffect(() => {
        if (triggerSave) {
            setTriggerSave(false);
        }
    }, [triggerSave, editorContent, assesseeOrgId]);

    // To auto save the one pager based on the event listener (Currently used while adding the comments thread in one pager)
    const save = () => {
        if (!editorContent.current?.length || !assesseeOrgId || !user.investorOrgId) return;
        const data = { assesseeOrgId, investorOrgId: user.investorOrgId, onePagerData: editorContent.current, email: user.primaryEmail };
        saveOnePagerTemplateData(data).catch(console.error);
    };

    /**
     * Generates a standalone HTML element from Slate JSON content for PDF export.
     * This approach avoids DOM cloning issues and ensures CSS is properly embedded.
     */
    const generateHTMLFromSlateContent = async () => {
        try {
            // Clean trailing empty paragraphs before conversion to prevent extra page
            const cleanedContent = editorContent.current.filter((node: any, index: number, arr: any[]) => {
                if (index === arr.length - 1) {
                    return !(node.type === 'paragraph' && node.children?.length === 1 && node.children[0]?.text === '');
                }
                return true;
            });

            // Generate HTML content from cleaned Slate JSON
            const htmlContent = slateToHtml(cleanedContent.length > 0 ? cleanedContent : editorContent.current);

            // Get logo if available
            let logoHtml = '';
            let footerHtml = '';

            if (mainImageUrl) {
                const response: any = await fetchImageFromS3AndConvertToBase64(mainImageUrl);
                if (response) {
                    logoHtml = `
                        <div style="display: flex; justify-content: flex-end; margin-bottom: 16px;">
                            <img src="${response}" alt="" style="height: 30px;" crossorigin="true" />
                        </div>
                    `;
                    footerHtml = `
                        <div style="font-size: 20px; font-weight: 500; color: #D2D2D2; margin-top: 16px;">
                            Built on AICA by ${orgName}
                        </div>
                    `;
                }
            }

            // Create a container element with embedded styles
            // Use display: inline-block to wrap content tightly (prevents extra page from watermark)
            const container = document.createElement('div');
            container.style.display = 'inline-block';
            container.style.width = '100%';
            container.style.margin = '0';
            container.style.padding = '0';
            container.style.position = 'relative';

            // Everything is inside the content div with proper layering
            container.innerHTML = `
                <style>
                    ${getPdfExportStyles()}
                    /* Prevent trailing br from adding extra space */
                    br:last-child { display: none; }
                    .content > br:last-child { display: none; }
                    .content > *:last-child { margin-bottom: 0 !important; }
                    
                    /* Watermark layer behind content */
                    .watermark-layer {
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background-image: ${aicaGreyBase64_2};
                        background-repeat: repeat;
                        background-size: 250px auto;
                        z-index: 0;
                        pointer-events: none;
                    }
                    
                    /* Content above watermark */
                    .content {
                        position: relative;
                        z-index: 1;
                        padding: 10px;
                    }
                </style>
                <div class="watermark-layer"></div>
                <div class="content">
                    ${logoHtml}
                    ${htmlContent}
                    ${footerHtml}
                </div>
            `;

            return container;
        } catch (err) {
            console.error('Error generating HTML from Slate content', err);
            // Fallback to DOM cloning if JSON conversion fails
            return getFallbackHTMLElement();
        }
    };

    /**
     * Fallback: Clone DOM element (original approach) if JSON conversion fails.
     */
    const getFallbackHTMLElement = async () => {
        const el = document?.getElementById('element-to-be-downloaded-as-pdf');
        const clonedElement = el?.cloneNode(true) as HTMLElement;

        if (clonedElement) {
            // Create a wrapper with proper layering
            const wrapper = document.createElement('div');
            wrapper.style.position = 'relative';
            wrapper.style.display = 'inline-block';
            wrapper.style.width = '100%';

            // Watermark layer
            const watermarkLayer = document.createElement('div');
            watermarkLayer.style.position = 'absolute';
            watermarkLayer.style.top = '0';
            watermarkLayer.style.left = '0';
            watermarkLayer.style.right = '0';
            watermarkLayer.style.bottom = '0';
            watermarkLayer.style.backgroundImage = aicaGreyBase64_2;
            watermarkLayer.style.backgroundRepeat = 'repeat';
            watermarkLayer.style.backgroundSize = '250px auto';
            watermarkLayer.style.zIndex = '0';
            watermarkLayer.style.pointerEvents = 'none';

            // Content styling
            clonedElement.style.position = 'relative';
            clonedElement.style.zIndex = '1';
            clonedElement.style.margin = '0';
            clonedElement.style.padding = '10px';

            if (mainImageUrl) {
                const response: any = await fetchImageFromS3AndConvertToBase64(mainImageUrl);
                if (response) {
                    const topDiv = document.createElement('div');
                    topDiv.style.display = 'flex';
                    topDiv.style.justifyContent = 'flex-end';
                    topDiv.style.marginBottom = '16px';

                    const topImg = document.createElement('img');
                    topImg.src = response;
                    topImg.alt = '';
                    topImg.style.height = '30px';
                    topImg.crossOrigin = 'true';
                    topDiv.appendChild(topImg);

                    const bottomDiv = document.createElement('div');
                    bottomDiv.style.fontSize = '20px';
                    bottomDiv.style.fontWeight = '500';
                    bottomDiv.style.color = '#D2D2D2';
                    bottomDiv.style.marginTop = '16px';
                    bottomDiv.innerHTML = `<span>Built on AICA by ${orgName}</span>`;

                    clonedElement.insertBefore(topDiv, clonedElement.firstChild);
                    clonedElement.appendChild(bottomDiv);
                }
            }

            wrapper.appendChild(watermarkLayer);
            wrapper.appendChild(clonedElement);
            return wrapper;
        }
        return clonedElement;
    };

    const removePageBreaks = () => {
        const elements = document.querySelectorAll('#page-break-line');
        elements.forEach((hr, index) => {
            const hrElement = hr as HTMLElement;
            hrElement.style.visibility = 'hidden';
        });
    };

    const showPageBreaks = () => {
        const elements = document.querySelectorAll('#page-break-line');
        elements.forEach((hr, index) => {
            const hrElement = hr as HTMLElement;
            hrElement.style.visibility = 'visible';
        });
    };

    const handlePreview = useReactToPrint({
        onPrintError: (error) => console.error(error),
        content: () => contentToPrint.current,
        documentTitle: 'Print This Document',
        removeAfterPrint: true,
        print: async (printIframe: HTMLIFrameElement) => {
            removePageBreaks();
            const getElementForExport = await generateHTMLFromSlateContent();

            try {
                // setLoadingPreview(true);
                const document = printIframe.contentDocument;
                if (document) {
                    // Use shared PDF export options
                    const opt = getDefaultPdfOptions('deal_summary.pdf');
                    html3pdf()
                        .from(getElementForExport)
                        .set(opt)
                        .toPdf()
                        .output('bloburl')
                        .then((bloburl) => {
                            window.open(bloburl, '_blank');
                            setLoadingPreview(false);
                        });
                }
            } catch (err) {
                console.error('Error in generating preview', err);
            } finally {
                showPageBreaks();
            }
        },
    });

    const handleSaveToDb = useReactToPrint({
        onPrintError: (error) => console.error('React to print error: ', error),
        content: () => contentToPrint.current,
        documentTitle: 'Print This Document',
        removeAfterPrint: true,
        print: async (printIframe: HTMLIFrameElement) => {
            try {
                const document = printIframe.contentDocument;
                const getElementForExport = await generateHTMLFromSlateContent();

                if (document) {
                    const html = document.getElementById('element-to-be-downloaded-as-pdf');

                    // Use shared PDF export options
                    const opt = getDefaultPdfOptions('one_pager_report.pdf');

                    html3pdf()
                        .from(getElementForExport)
                        .set(opt)
                        .toPdf()
                        .output('blob')
                        .then(function (res) {
                            var file = new File([res], 'one_pager_report.pdf', {
                                type: 'application/pdf',
                            });
                            getSignedUrlFromS3(file);
                        });
                }
            } catch (err) {
                console.error('Error in saving File', err);
            }
        },
    });

    const getSignedUrlFromS3 = async (file) => {
        const uploadConfig = {
            assesseeOrgId: assesseeOrgId,
            categoryId: categoryId,
            mnemosyneOrgId: domainMetadata?.investorOrgId,
        };
        const fileData = {
            file: {
                name: `${companyName ? companyName + '-' : ''} Brief Profile - ${getTimeStampForFileName()}.pdf`,
                type: 'application/pdf',
                size: file.size,
            },
        };
        try {
            const signResponse = await getSignedUrl(fileData, uploadConfig);
            uploadFileAfter(signResponse.signedUrl, file, fileData);
        } catch (err) {
            setSaving(false);
            console.error('Error in getting signed url from s3', err);
            toast.error('Some error occoured, please try again later');
        }
    };

    const handleClose = () => {
        dispatch(closeCommentsThread());
        if (source !== CREATE_DEAL) {
            setTriggerSave(true);
            saveOnePagerTemplateDataForOrg(editorContent.current);
        } else if (source === CREATE_DEAL) {
            setSaving(true);
            handleSaveToDb(null, () => contentToPrint?.current);
        } else {
            closeModal();
        }

        Mixpanel.track(AICA_ONEPAGER_CLOSED, {
            userRole: user?.accountType,
            pnoPlatform: getSubdomain(),
            pnoId: domainMetadata?.investorOrgId,
            userId: user?.userId,
            currentUrl: window.location.href,
            organizationId: assesseeOrgId,
        });
    };

    const uploadFileAfter = async (url, file, fileData) => {
        const uploadResponse = await uploadFile(file, url);
        const input_saveMetadata = {
            categoryId: categoryId,
            mnemosyneOrgId: domainMetadata?.investorOrgId,
            fileName: fileData.file.name,
            fileSizeKb: fileData?.file?.size / 1_000,
            flowId: flowId,
            fileType: fileData.file.type,
        };
        const docData = await saveDocMetaDataInDocService({
            input: input_saveMetadata,
            orgId: assesseeOrgId,
        });

        const fileInfo = {
            url: uploadResponse?.url,
            fileName: fileData.file.name,
            docId: docData?.id,
        };
        metadataUpdate(fileInfo);
        setSaving(false);
        toast.success('Successfully Saved');
        closeModal();
    };

    function generateUserMessage(userInfo: { userName: string; onePagerData: { lastUpdatedAt: string } }) {
        if (!userInfo?.userName || !userInfo?.onePagerData?.lastUpdatedAt) {
            return '';
        }
        const formattedDate = dayjs(userInfo.onePagerData.lastUpdatedAt).format(TIME_DATE_FORMAT);

        return `Last edited on ${formattedDate} by ${userInfo.userName}`;
    }

    const handlePreviewClick = (e) => {
        e.preventDefault();
        setLoadingPreview(true);
        handlePreview(null, () => contentToPrint?.current);

        Mixpanel.track(AICA_ONEPAGER_PREVIEW, {
            userRole: user?.accountType,
            pnoPlatform: getSubdomain(),
            pnoId: domainMetadata?.investorOrgId,
            userId: user?.userId,
            currentUrl: window.location.href,
            organizationId: assesseeOrgId,
        });
    };

    const handleSaveAndClose = (e) => {
        e.preventDefault();
        setSaving(true);
        setTriggerSave(true);
        dispatch(closeCommentsThread());
        if (source === CREATE_DEAL) {
            handleSaveToDb(null, () => contentToPrint?.current);
        }
        saveOnePagerTemplateDataForOrg(editorContent.current);

        Mixpanel.track(AICA_ONEPAGER_CLOSED, {
            userRole: user?.accountType,
            pnoPlatform: getSubdomain(),
            pnoId: domainMetadata?.investorOrgId,
            userId: user?.userId,
            currentUrl: window.location.href,
            organizationId: assesseeOrgId,
        });
    };

    // Add loading state UI for regeneration
    const renderRegenerateButton = () => {
        return (
            <div
                className={styles.Regen}
                onClick={() => {
                    if (!generating) {
                        setShowRegenConfirmModal(true);

                        Mixpanel.track(AICA_ONEPAGER_REGENERATE, {
                            userRole: user?.accountType,
                            pnoPlatform: getSubdomain(),
                            pnoId: domainMetadata?.investorOrgId,
                            userId: user?.userId,
                            currentUrl: window.location.href,
                            organizationId: assesseeOrgId,
                        });
                    }
                }}
            >
                {generating ? (
                    <>
                        <LoadingSpinner color="var(--primary-text-colour)" height="18px" style={{ margin: 0 }} />
                        <span>Regenerating</span>
                    </>
                ) : (
                    <>
                        <img src={regenerateIcon} alt={'re'} height={'24px'} />
                        <span>Regenerate</span>
                    </>
                )}
            </div>
        );
    };

    return openModal ? (
        <Modal
            size="full"
            open={openModal}
            style={{ padding: 0, fontSize: '12px', lineHeight: '140%', position: 'absolute' }}
            persistent
            withoutCloseButton
        >
            <div className={styles.Container}>
                <div className={styles.Top}>
                    {!!editorReady && (
                        <SlateEditor
                            source={source}
                            editorInitialData={editorInitialData}
                            targetRef={contentToPrint}
                            editorContentRef={editorContent}
                            assesseeOrgId={assesseeOrgId}
                            generating={generating}
                            onClose={handleClose}
                            savingData={savingData}
                            editorType={TEXT_EDITOR_TYPES.ONE_PAGER}
                        />
                    )}
                    <div className={styles.Footer}>
                        <div>{renderRegenerateButton()}</div>

                        <div className={styles.Text}>*Right-click tables to modify: add rows, add columns, or delete</div>

                        <div className={styles.Text}>{generateUserMessage(userInfo)}</div>

                        <div className={styles.Buttons}>
                            <Button
                                text="Preview"
                                variant="purpleinverted"
                                onClick={handlePreviewClick}
                                style={{ margin: '0 20px 0 0 ' }}
                                loading={loadingPreview}
                            />
                            <Button
                                text="Save & Close"
                                variant="primary"
                                onClick={handleSaveAndClose}
                                style={{ marginTop: '0' }}
                                loading={saving}
                            />
                        </div>
                    </div>
                </div>

                <div className={styles.Comments} id="onepager-comment-container">
                    {/* //Container Element Where comments thread is rendered via portal */}
                </div>
            </div>
        </Modal>
    ) : (
        <></>
    );
}

export default DealSummaryEditor;
